export enum AdminOrderFilter {
  ACTIVE = 'ACTIVE',
  COMPLETED_ALL = 'COMPLETED_ALL',
  ALL = 'ALL',
  PENDING = 'PENDING',
  RESTAURANT_NOT_ACCEPTED = 'RESTAURANT_NOT_ACCEPTED',
  RESTAURANT_COOKING = 'RESTAURANT_COOKING',
  WITHOUT_DRIVER = 'WITHOUT_DRIVER',
  WAITING_PICKUP = 'WAITING_PICKUP',
  IN_DELIVERY = 'IN_DELIVERY',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  GROUP_ACTIVE_ORDERS = 'GROUP_ACTIVE_ORDERS',
  GROUP_FINISHED_ORDERS = 'GROUP_FINISHED_ORDERS'
}

export enum OrderStatus {
  ADMIN_CANCELLED_ORDER = 'ADMIN_CANCELLED_ORDER',
  ORDER_PLACED = 'ORDER_PLACED',
  ORDER_ACCEPTED_BY_ADMIN = 'ORDER_ACCEPTED_BY_ADMIN',
  RESTAURANT_ACCEPTED_ORDER = 'RESTAURANT_ACCEPTED_ORDER',
  RESTAURANT_FINISHED_ORDER = 'RESTAURANT_FINISHED_ORDER',
  DRIVER_PICKED_UP_ORDER = 'DRIVER_PICKED_UP_ORDER',
  DRIVER_DROPPED_OFF_ORDER = 'DRIVER_DROPPED_OFF_ORDER',
  ORDER_COMPLETED = 'ORDER_COMPLETED'
}

export interface OrderRestaurantResponse {
  id: number;
  name: string;
  phone: string;
}

export interface ClientResponse {
  id: number;
  name: string | null;
  phone: string;
}

export interface OrderAssignedDriverResponse {
  id: number;
  fullName: string;
  phone: string;
}

export interface AdminOrderResponse {
  id: number;
  restaurant: OrderRestaurantResponse;
  client: ClientResponse;
  assignedDriver: OrderAssignedDriverResponse | null;
  totalPrice: number;
  orderStatus: OrderStatus;
  createdAt: number;
  restaurantRejectionReason: string;
  problematicCompleteReason: string | null;
}

export interface OrdersRequest {
  searchQuery: string;
  filter: AdminOrderFilter;
  page: number;
  pageSize: number;
}

export interface OrdersResponse {
  orders: AdminOrderResponse[];
  totalPages: number;
  totalElements: number;
  currentPage: number;
}

// Order Details Types
export enum OrderStatusChange {
  ORDER_PLACED = 'ORDER_PLACED',
  ADMIN_ACCEPTED_ORDER = 'ADMIN_ACCEPTED_ORDER',
  ADMIN_ASSIGNED_ORDER_TO_BRANCH = 'ADMIN_ASSIGNED_ORDER_TO_BRANCH',
  ADMIN_CHANGED_BRANCH = 'ADMIN_CHANGED_BRANCH',
  ADMIN_SENT_ORDER_TO_DRIVER = 'ADMIN_SENT_ORDER_TO_DRIVER',
  ADMIN_DETACHED_ORDER_FROM_DRIVER = 'ADMIN_DETACHED_ORDER_FROM_DRIVER',
  SYSTEM_SENT_ORDER_TO_DRIVERS = 'SYSTEM_SENT_ORDER_TO_DRIVERS',
  DRIVER_ACCEPTED_ORDER = 'DRIVER_ACCEPTED_ORDER',
  RESTAURANT_REJECTED_ORDER = 'RESTAURANT_REJECTED_ORDER',
  RESTAURANT_ACCEPTED_ORDER = 'RESTAURANT_ACCEPTED_ORDER',
  RESTAURANT_FINISHED_ORDER = 'RESTAURANT_FINISHED_ORDER',
  DRIVER_PICKED_UP_ORDER = 'DRIVER_PICKED_UP_ORDER',
  DRIVER_DROPPED_OFF_ORDER = 'DRIVER_DROPPED_OFF_ORDER',
  ADMIN_CANCELLED_ORDER = 'ADMIN_CANCELLED_ORDER',
  ORDER_COMPLETED = 'ORDER_COMPLETED'
}

export interface OrderRestaurantBranchResponse {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  representativeName: string;
  phone: string;
}

export interface OrderedFoodResponse {
  id: number;
  name: string;
  price: number;
  count: number;
  imageId: string | null;
}

export interface OrderStatusChangeResponse {
  id: number;
  orderId: number;
  orderStatusChange: OrderStatusChange;
  reason: string;
  message: string;
  datetime: number;
}

export interface AdminOrderDetailsResponse {
  id: number;
  client: ClientResponse;
  clientLocationName: string;
  clientLatitude: number;
  clientLongitude: number;
  clientExtraNotesForDriver: string;
  clientExtraNotesForRestaurant: string;
  contactPhone: string;
  restaurant: OrderRestaurantResponse;
  assignedRestaurantBranch: OrderRestaurantBranchResponse | null;
  allRestaurantBranches: OrderRestaurantBranchResponse[];
  deliveryPrice: number;
  orderedFoods: OrderedFoodResponse[];
  assignedDriver: OrderAssignedDriverResponse | null;
  orderStatus: OrderStatus;
  createdAt: number;
  statusChanges: OrderStatusChangeResponse[];
  orderDriverConfirmationCode: string;
  shouldDriverConfirmOrderCode: boolean;
  canClientCancelOrder: boolean;
  restaurantRejectionReason: string;
  problematicCompleteReason: string | null;
  driverPricePercentageForPlatform: number;
  restaurantFoodPricePercentageForPlatform: number;
}

// Admin Actions Types
export interface AcceptOrderRequest {
  orderId: number;
  assignedBranchId: number;
}

export interface CancelOrderRequest {
  orderId: number;
  reason: string;
}

export interface ChangeOrderRestaurantBranchRequest {
  orderId: number;
  branchId: number;
  reason: string;
}

export interface DetachOrderDriverRequest {
  orderId: number;
  reason: string;
}

export interface SendOrderToDriverRequest {
  orderId: number;
  driverId: number;
}

export interface SendMessageToClientRequest {
  clientId: number;
  message: string;
}

export interface UpdateOrderDeliveryPriceRequest {
  orderId: number;
  deliveryPrice: number;
}

export interface ProblematicCompleteOrderRequest {
  orderId: number;
  reason: string;
}

// Driver Path Types
export interface LatLngPoint {
  latitude: number;
  longitude: number;
}

export interface DriverPathResponse {
  path: LatLngPoint[];
}

// Create Order Types
export interface FoodOrder {
  foodId: number;
  count: number;
}

export interface CreateOrderRequest {
  clientLocationName: string;
  clientLatitude: number;
  clientLongitude: number;
  clientExtraNotesForDriver: string;
  clientExtraNotesForRestaurant: string;
  contactPhone: string;
  foods: FoodOrder[];
}

export interface OrderCreatedResponse {
  orderId: number;
}

export interface RestaurantSearchSuggestion {
  id: number;
  name: string;
}

export interface RestaurantFoodResponse {
  id: number;
  name: string;
  description: string;
  price: number;
  isAvailable: boolean;
  foodImage: string | null;
}

export interface GetPreOrderPriceDetailsRequest {
  foods: FoodOrder[];
}

export interface FoodPrice {
  id: number;
  name: string;
  price: number;
  count: number;
}

export interface PreOrderPriceDetailsResponse {
  totalPrice: number;
  deliveryPrice: number;
  foodPrices: FoodPrice[];
}

export interface OrderMetadata {
  id: number;
  key: string;
  value: string;
  createdAt: number;
  orderId: number;
}